import React from 'react'
import { t } from 'ttag'
import { DivisionCategoryType } from '../../services/api.types'
import { getDivisionCategoryTypeTranslation } from '../../views/TeamManagement/TeamManagement'
import YumSelect from '../YumSelect/YumSelect'

interface IDivisionCategoryTypeSelectProps {
  /** Current selected category type */
  value: DivisionCategoryType
  /** Callback when category type changes */
  onChange: (type: DivisionCategoryType) => void
  /** Set of category types that are already used (will be disabled) */
  usedCategoryTypes: Set<DivisionCategoryType>
  /** Label for the select field */
  label?: string
  /** Additional styles for the select */
  style?: React.CSSProperties
  /** Whether to include CUSTOM option */
  includeCustom?: boolean
}

/**
 * Reusable component for selecting division category types
 * Handles the logic for showing "(added)" text for used categories
 * and provides consistent translation handling
 */
export default function DivisionCategoryTypeSelect({
  value,
  onChange,
  usedCategoryTypes,
  label = t`Group type`,
  style,
  includeCustom = true,
}: IDivisionCategoryTypeSelectProps) {
  // Get all category types, optionally excluding CUSTOM
  const categoryTypes = Object.values(DivisionCategoryType).filter(
    (type) => includeCustom || type !== DivisionCategoryType.CUSTOM,
  )

  return (
    <YumSelect
      label={label}
      value={value}
      onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
        onChange(e.target.value as DivisionCategoryType)
      }}
      style={style}
    >
      {categoryTypes.map((type) => {
        const isUsed = usedCategoryTypes.has(type)
        const displayText = getDivisionCategoryTypeTranslation(type, true)

        return (
          <option key={type} value={type} disabled={isUsed}>
            {isUsed ? `${displayText} (${t`added`})` : displayText}
          </option>
        )
      })}
    </YumSelect>
  )
}
